import { Component, type ErrorInfo, type ReactNode } from 'react';
import { i18n } from '@tarifdata/i18n';

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  error: Error | null;
}

export class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Unhandled Web application error', error, info.componentStack);
  }

  private reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <main
        className="min-h-screen bg-md-surface px-4 py-16 text-md-on-surface"
        role="alert"
      >
        <div className="mx-auto max-w-lg rounded-md-lg bg-md-error-container p-6 shadow-md-1">
          <div className="mb-3 text-4xl" aria-hidden="true">⚠️</div>
          <h1 className="text-xl font-semibold">{i18n.t('errors.appError')}</h1>
          <p className="mt-2 text-sm text-md-on-surface-variant">
            {i18n.t('errors.unexpected')}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button type="button" onClick={this.reset} className="md-btn-filled">
              {i18n.t('errors.tryAgain')}
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="md-btn-outlined"
            >
              {i18n.t('errors.reload')}
            </button>
          </div>
        </div>
      </main>
    );
  }
}
